import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import { toast } from 'sonner';
import { TrendingUp, ArrowDownRight, ArrowUpRight, PiggyBank, ExternalLink } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const fmt = (n) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n) || 0);

const CATEGORY_COLORS = ['#0F172A', '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];

const Spending = () => {
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [recent, setRecent] = useState([]);
  const [budgetStatus, setBudgetStatus] = useState([]);
  const [savingsTarget, setSavingsTarget] = useState(20);
  const [targetDraft, setTargetDraft] = useState('20');
  const [savingTarget, setSavingTarget] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, t, c, tx, st, bs] = await Promise.all([
        axios.get(`${API}/analytics/summary`),
        axios.get(`${API}/analytics/trends`),
        axios.get(`${API}/analytics/by-category?type=expense`),
        axios.get(`${API}/transactions`),
        axios.get(`${API}/wealth/savings-target`),
        axios.get(`${API}/budgets/status`).catch(() => ({ data: [] })),
      ]);
      setSummary(s.data);
      setTrends((t.data || []).slice(-6));
      setByCategory([...(c.data || [])].sort((a, b) => b.amount - a.amount).slice(0, 6));
      setRecent((tx.data || []).slice(0, 8));
      setSavingsTarget(st.data?.target_pct ?? 20);
      setTargetDraft(String(st.data?.target_pct ?? 20));
      setBudgetStatus(bs.data || []);
    } catch (e) {
      toast.error('Failed to load spending data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveTarget = async () => {
    const n = parseFloat(targetDraft);
    if (isNaN(n) || n < 0 || n > 100) {
      toast.error('Target must be between 0 and 100');
      return;
    }
    setSavingTarget(true);
    try {
      await axios.put(`${API}/wealth/savings-target`, { target_pct: n });
      setSavingsTarget(n);
      toast.success('Savings target updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingTarget(false);
    }
  };

  if (loading) {
    return <div className="p-8 md:p-12 font-chivo text-xl" data-testid="spending-loading">Loading spending…</div>;
  }

  const income = summary?.summary?.total_income || 0;
  const expenses = summary?.summary?.total_expenses || 0;
  const net = income - expenses;
  const savingsRate = income > 0 ? Math.max(0, Math.min(100, (net / income) * 100)) : 0;
  const targetStatus = savingsRate >= savingsTarget
    ? { label: 'On target', color: 'bg-emerald-500', text: 'text-emerald-600' }
    : savingsRate >= savingsTarget * 0.75
    ? { label: 'Close', color: 'bg-amber-500', text: 'text-amber-600' }
    : { label: 'Below target', color: 'bg-red-500', text: 'text-red-600' };

  // Build a quick lookup: category → budget status for in-line flags
  const budgetByCategory = {};
  (budgetStatus || []).forEach((b) => { budgetByCategory[(b.category || '').toLowerCase()] = b; });

  // Monthly rolling: use latest trend row as "this month"
  const latest = trends.length ? trends[trends.length - 1] : null;
  const prev = trends.length > 1 ? trends[trends.length - 2] : null;
  const latestExpenses = latest?.expenses || 0;
  const prevExpenses = prev?.expenses || 0;
  const momDelta = prevExpenses > 0 ? ((latestExpenses - prevExpenses) / prevExpenses) * 100 : 0;

  return (
    <div className="p-8 md:p-12" data-testid="spending-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="spending-title">Spending</h1>
            <p className="text-slate-600 mt-1 font-ibm">Cash flow, savings rate and where your money goes.</p>
          </div>
          <Link to="/transactions">
            <Button className="bg-slate-900 text-white rounded-sm hover:bg-slate-800" data-testid="manage-transactions-link">
              <ExternalLink size={14} className="mr-2" />Manage transactions
            </Button>
          </Link>
        </div>

        {/* HERO: SAVINGS RATE + INCOME + EXPENSES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="md:col-span-1 bg-slate-900 text-white p-8 rounded-sm" data-testid="savings-rate-card">
            <div className="flex items-center gap-2 mb-2">
              <PiggyBank size={16} className="text-slate-400" />
              <p className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-400">Savings rate</p>
            </div>
            <p className="text-5xl font-chivo font-bold mt-2 tabular-nums" data-testid="savings-rate-value">
              {savingsRate.toFixed(1)}%
            </p>
            <div className="mt-3 flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${targetStatus.color}`}></span>
              <span className="text-xs font-ibm text-slate-300" data-testid="savings-status">{targetStatus.label}</span>
            </div>
            <div className="mt-3 h-2 bg-slate-700 rounded-sm overflow-hidden relative">
              <div className={`h-full ${targetStatus.color}`} style={{ width: `${savingsRate}%` }} data-testid="savings-rate-bar" />
              {savingsTarget > 0 && savingsTarget <= 100 && (
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-white opacity-80"
                  style={{ left: `${savingsTarget}%` }}
                  title={`Target ${savingsTarget}%`}
                />
              )}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Input
                type="number"
                value={targetDraft}
                onChange={(e) => setTargetDraft(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white h-8 w-20"
                data-testid="savings-target-input"
              />
              <span className="text-xs font-ibm text-slate-400">% target</span>
              <Button
                onClick={saveTarget}
                disabled={savingTarget}
                className="bg-white text-slate-900 h-8 px-3 rounded-sm hover:bg-slate-100 ml-auto"
                data-testid="savings-target-save"
              >
                {savingTarget ? '…' : 'Save'}
              </Button>
            </div>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-sm" data-testid="income-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">Income</span>
              <ArrowUpRight size={18} className="text-emerald-600" />
            </div>
            <p className="text-3xl font-chivo font-bold text-slate-900 tabular-nums mt-2" data-testid="income-value">
              {fmt(income)}
            </p>
            <p className="text-xs font-ibm text-slate-500 mt-2">All time</p>
          </div>

          <div className="bg-white border border-slate-200 p-6 rounded-sm" data-testid="expenses-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">Expenses</span>
              <ArrowDownRight size={18} className="text-red-600" />
            </div>
            <p className="text-3xl font-chivo font-bold text-slate-900 tabular-nums mt-2" data-testid="expenses-value">
              {fmt(expenses)}
            </p>
            {prev && (
              <p className={`text-xs font-ibm mt-2 ${momDelta > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {momDelta > 0 ? '▲' : '▼'} {Math.abs(momDelta).toFixed(1)}% vs last month
              </p>
            )}
          </div>
        </div>

        {/* CASH-FLOW TREND */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="cashflow-chart-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-slate-600" />
            <h2 className="text-xl font-chivo font-bold text-slate-900">Monthly cash flow</h2>
          </div>
          {trends.length < 2 ? (
            <div className="py-10 text-center border border-dashed border-slate-200 rounded-sm">
              <p className="text-sm font-ibm text-slate-500" data-testid="cashflow-empty">
                Add a few months of transactions to see your cash-flow trend.
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trends}>
                <defs>
                  <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" style={{ fontSize: '11px' }} />
                <YAxis stroke="#64748B" style={{ fontSize: '11px' }} tickFormatter={(v) => fmt(v).replace('.00', '')} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '2px', color: '#F8FAFC' }}
                  formatter={(v) => fmt(v)}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Area type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} fill="url(#incomeGrad)" name="Income" />
                <Area type="monotone" dataKey="expenses" stroke="#EF4444" strokeWidth={2} fill="url(#expenseGrad)" name="Expenses" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* CATEGORIES + RECENT */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="top-categories-card">
            <h2 className="text-xl font-chivo font-bold text-slate-900 mb-4">Top spending categories</h2>
            {byCategory.length === 0 ? (
              <p className="text-sm font-ibm text-slate-500 py-10 text-center" data-testid="no-categories">
                No expenses yet.
              </p>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={byCategory} layout="vertical" margin={{ left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" stroke="#64748B" style={{ fontSize: '11px' }} tickFormatter={(v) => fmt(v).replace('.00', '')} />
                    <YAxis dataKey="category" type="category" stroke="#64748B" style={{ fontSize: '11px' }} width={100} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '2px', color: '#F8FAFC' }}
                      formatter={(v) => fmt(v)}
                    />
                    <Bar dataKey="amount" radius={[0, 2, 2, 0]}>
                      {byCategory.map((_, idx) => (
                        <rect key={idx} fill={CATEGORY_COLORS[idx % CATEGORY_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <ul className="mt-3 space-y-1" data-testid="categories-list">
                  {byCategory.map((c) => {
                    const budget = budgetByCategory[(c.category || '').toLowerCase()];
                    const overBudget = budget && budget.status === 'over';
                    const warning = budget && budget.status === 'warning';
                    return (
                      <li key={c.category} className="flex justify-between items-center text-sm font-ibm text-slate-700 py-1 border-t border-slate-100" data-testid={`category-${c.category}`}>
                        <span className="capitalize flex items-center gap-2">
                          {c.category}
                          {overBudget && (
                            <span className="px-1.5 py-0.5 text-[10px] font-ibm font-bold uppercase tracking-widest bg-red-100 text-red-700 rounded-sm" data-testid={`over-budget-${c.category}`}>
                              Over
                            </span>
                          )}
                          {!overBudget && warning && (
                            <span className="px-1.5 py-0.5 text-[10px] font-ibm font-bold uppercase tracking-widest bg-amber-100 text-amber-700 rounded-sm" data-testid={`warning-budget-${c.category}`}>
                              Near limit
                            </span>
                          )}
                        </span>
                        <span className="tabular-nums font-semibold text-slate-900">
                          {fmt(c.amount)}
                          {budget && (
                            <span className="text-xs font-ibm text-slate-500 ml-1">/ {fmt(budget.monthly_limit)}</span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="recent-txns-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-chivo font-bold text-slate-900">Recent transactions</h2>
              <Link to="/transactions" className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600 hover:text-slate-900">
                All →
              </Link>
            </div>
            {recent.length === 0 ? (
              <p className="text-sm font-ibm text-slate-500 py-10 text-center" data-testid="no-recent">
                No transactions yet. <Link to="/transactions" className="underline">Add your first →</Link>
              </p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="recent-list">
                {recent.map((t) => (
                  <li key={t.id} className="flex justify-between items-center py-3" data-testid={`recent-${t.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-ibm font-medium text-slate-900 truncate">{t.description}</p>
                      <p className="text-xs text-slate-500 uppercase tracking-widest font-ibm mt-0.5">
                        {t.category} · {new Date(t.date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`font-chivo font-bold tabular-nums ${t.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {t.type === 'income' ? '+' : '-'}{fmt(t.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* HOW IT CONNECTS */}
        <div className="mt-6 bg-indigo-50 border-l-4 border-indigo-500 p-5 rounded-sm" data-testid="savings-insight-banner">
          <p className="text-sm font-ibm text-slate-800">
            <strong>How this connects to your wealth:</strong> every dollar you don't spend becomes a dollar that compounds in your portfolio.
            Hitting a <strong>20% savings rate</strong> is the single biggest lever on your retirement goal.
            Head to <Link to="/wealth/goals" className="font-semibold underline">Goals</Link> to see what your current savings rate implies for your target date.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Spending;
